import { useState, useRef } from 'react'
import { Upload, Download, Settings, Image as ImageIcon, AlertCircle, X, ZoomIn } from 'lucide-react'

interface WatermarkSettings {
  text: string
  position: string
  opacity: number
  fontSize: number
}

const WatermarkTool = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [resultUrl, setResultUrl] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string>('')
  const [showImageModal, setShowImageModal] = useState(false)
  const [modalImageUrl, setModalImageUrl] = useState<string>('')
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [lastTouchDistance, setLastTouchDistance] = useState(0)
  const [settings, setSettings] = useState<WatermarkSettings>({
    text: '水印文字',
    position: 'bottom-right',
    opacity: 128,
    fontSize: 36,
  })
  
  const fileInputRef = useRef<HTMLInputElement>(null)

  const positions = [
    { value: 'top-left', label: '左上角' },
    { value: 'top-right', label: '右上角' },
    { value: 'bottom-left', label: '左下角' },
    { value: 'bottom-right', label: '右下角' },
    { value: 'center', label: '居中' },
  ]

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('请选择图片文件')
        return
      }
      
      setSelectedFile(file)
      setError('')
      if (resultUrl) URL.revokeObjectURL(resultUrl)
      setResultUrl('')
      
      // 创建预览URL
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const file = event.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file)
      setError('')
      if (resultUrl) URL.revokeObjectURL(resultUrl)
      setResultUrl('')
      
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    } else {
      setError('请拖拽图片文件')
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const processWatermark = async () => {
    if (!selectedFile) {
      setError('请先选择图片文件')
      return
    }

    if (!settings.text.trim()) {
      setError('请输入水印文字')
      return
    }

    setIsProcessing(true)
    setError('')

    try {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          setError('浏览器不支持画布')
          setIsProcessing(false)
          return
        }
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        ctx.drawImage(img, 0, 0)

        const transparency = Math.max(0, Math.min(255, settings.opacity)) / 255
        ctx.globalAlpha = 1 - transparency
        ctx.font = `${settings.fontSize}px sans-serif`
        const padding = Math.round(settings.fontSize * 0.5)
        let x = padding
        let y = padding
        let textAlign: CanvasTextAlign = 'left'
        let textBaseline: CanvasTextBaseline = 'top'
        switch (settings.position) {
          case 'top-left':
            textAlign = 'left'
            textBaseline = 'top'
            x = padding
            y = padding
            break
          case 'top-right':
            textAlign = 'right'
            textBaseline = 'top'
            x = canvas.width - padding
            y = padding
            break
          case 'bottom-left':
            textAlign = 'left'
            textBaseline = 'bottom'
            x = padding
            y = canvas.height - padding
            break
          case 'bottom-right':
            textAlign = 'right'
            textBaseline = 'bottom'
            x = canvas.width - padding
            y = canvas.height - padding
            break
          case 'center':
            textAlign = 'center'
            textBaseline = 'middle'
            x = canvas.width / 2
            y = canvas.height / 2
            break
          default:
            textAlign = 'right'
            textBaseline = 'bottom'
            x = canvas.width - padding
            y = canvas.height - padding
        }
        ctx.textAlign = textAlign
        ctx.textBaseline = textBaseline
        ctx.strokeStyle = 'rgba(0,0,0,1)'
        ctx.lineWidth = Math.max(1, Math.floor(settings.fontSize / 12))
        ctx.strokeText(settings.text, x, y)
        ctx.fillStyle = 'rgba(255,255,255,1)'
        ctx.fillText(settings.text, x, y)

        canvas.toBlob(
          (blob) => {
            if (blob) {
              if (resultUrl) URL.revokeObjectURL(resultUrl)
              const url = URL.createObjectURL(blob)
              setResultUrl(url)
            } else {
              setError('生成图片失败')
            }
            setIsProcessing(false)
          },
          'image/png',
          0.92
        )
      }
      img.onerror = () => {
        setError('加载图片失败')
        setIsProcessing(false)
      }
      img.src = previewUrl
    } catch (err) {
      setError('处理图片时发生错误，请重试')
      setIsProcessing(false)
    }
  }

  const downloadResult = () => {
    if (resultUrl) {
      const link = document.createElement('a')
      link.href = resultUrl
      link.download = `watermarked_${selectedFile?.name || 'image.png'}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const resetTool = () => {
    setSelectedFile(null)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl('')
    if (resultUrl) URL.revokeObjectURL(resultUrl)
    setResultUrl('')
    setError('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const openImageModal = (imageUrl: string) => {
    setModalImageUrl(imageUrl)
    setShowImageModal(true)
    setScale(1)
    setPosition({ x: 0, y: 0 })
    // 防止滚动穿透
    document.body.style.overflow = 'hidden'
  }

  const closeImageModal = () => {
    setShowImageModal(false)
    setModalImageUrl('')
    setScale(1)
    setPosition({ x: 0, y: 0 })
    setIsDragging(false)
    // 恢复滚动
    document.body.style.overflow = 'unset'
  }

  // 获取两点间距离（用于双指缩放）
  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0
    const touch1 = touches[0]
    const touch2 = touches[1]
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) + 
      Math.pow(touch2.clientY - touch1.clientY, 2)
    )
  }

  // 获取两点中心（用于双指缩放中心点）
  const getTouchCenter = (touches: React.TouchList) => {
    if (touches.length < 2) return { x: 0, y: 0 }
    const touch1 = touches[0]
    const touch2 = touches[1]
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    }
  }

  // 鼠标滚轮缩放
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    const centerX = e.clientX - rect.left - rect.width / 2
    const centerY = e.clientY - rect.top - rect.height / 2
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    const newScale = Math.max(0.5, Math.min(5, scale * delta))
    
    // 以鼠标位置为中心缩放
    const scaleChange = newScale / scale
    setPosition({
      x: position.x - centerX * (scaleChange - 1),
      y: position.y - centerY * (scaleChange - 1)
    })
    setScale(newScale)
  }

  // 鼠标拖拽开始
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) { // 左键
      setIsDragging(true)
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
    }
  }

  // 鼠标拖拽
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }

  // 鼠标拖拽结束
  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // 触摸开始
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // 双指缩放
      const distance = getTouchDistance(e.touches)
      setLastTouchDistance(distance)
    } else if (e.touches.length === 1) {
      // 单指拖拽
      const touch = e.touches[0]
      setIsDragging(true)
      setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y })
    }
  }

  // 触摸移动
  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    
    if (e.touches.length === 2) {
      // 双指缩放
      const distance = getTouchDistance(e.touches)
      if (lastTouchDistance > 0) {
        const scaleChange = distance / lastTouchDistance
        const newScale = Math.max(0.5, Math.min(5, scale * scaleChange))
        
        // 以双指中心为缩放中心
        const center = getTouchCenter(e.touches)
        const rect = e.currentTarget.getBoundingClientRect()
        const centerX = center.x - rect.left - rect.width / 2
        const centerY = center.y - rect.top - rect.height / 2
        
        const actualScaleChange = newScale / scale
        setPosition({
          x: position.x - centerX * (actualScaleChange - 1),
          y: position.y - centerY * (actualScaleChange - 1)
        })
        setScale(newScale)
      }
      setLastTouchDistance(distance)
    } else if (e.touches.length === 1 && isDragging) {
      // 单指拖拽
      const touch = e.touches[0]
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      })
    }
  }

  // 触摸结束
  const handleTouchEnd = () => {
    setIsDragging(false)
    setLastTouchDistance(0)
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">图片水印工具</h1>
        <p className="text-gray-600">为您的图片添加自定义文字水印，支持多种位置和样式设置</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 左侧：文件上传和设置 */}
        <div className="lg:col-span-1 space-y-6">
          {/* 文件上传区域 */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Upload className="mr-2" size={20} />
              上传图片
            </h3>
            
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors cursor-pointer"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">
                点击选择图片或拖拽到此处
              </p>
              <p className="text-sm text-gray-500">
                支持 JPG、PNG、GIF 等格式
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {selectedFile && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  已选择: {selectedFile.name}
                </p>
              </div>
            )}
          </div>

          {/* 水印设置 */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="mr-2" size={20} />
              水印设置
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  水印文字
                </label>
                <input
                  type="text"
                  value={settings.text}
                  onChange={(e) => setSettings({ ...settings, text: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="输入水印文字"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  水印位置
                </label>
                <select
                  value={settings.position}
                  onChange={(e) => setSettings({ ...settings, position: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {positions.map((pos) => (
                    <option key={pos.value} value={pos.value}>
                      {pos.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  透明度: {Math.round((settings.opacity / 255) * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="255"
                  value={settings.opacity}
                  onChange={(e) => setSettings({ ...settings, opacity: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  字体大小: {settings.fontSize}px
                </label>
                <input
                  type="range"
                  min="12"
                  max="100"
                  value={settings.fontSize}
                  onChange={(e) => setSettings({ ...settings, fontSize: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <button
                onClick={processWatermark}
                disabled={!selectedFile || isProcessing}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? '处理中...' : '添加水印'}
              </button>
              
              <button
                onClick={resetTool}
                className="w-full btn-secondary"
              >
                重置
              </button>
            </div>
          </div>
        </div>

        {/* 右侧：预览区域 */}
        <div className="lg:col-span-2">
          <div className="card h-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">预览</h3>
            
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <AlertCircle className="text-red-500 mr-2" size={20} />
                <span className="text-red-700">{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 原图预览 */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">原图</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 aspect-square flex items-center justify-center">
                  {previewUrl ? (
                    <div className="relative group cursor-pointer" onClick={() => openImageModal(previewUrl)}>
                      <img
                        src={previewUrl}
                        alt="原图预览"
                        className="max-w-full max-h-full object-contain"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                        <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" size={24} />
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-400 text-center">
                      <ImageIcon size={48} className="mx-auto mb-2" />
                      <p>请选择图片</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 处理结果 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">处理结果</h4>
                  {resultUrl && (
                    <button
                      onClick={downloadResult}
                      className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 text-sm"
                    >
                      <Download size={16} />
                      <span>下载</span>
                    </button>
                  )}
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 aspect-square flex items-center justify-center">
                  {resultUrl ? (
                    <div className="relative group cursor-pointer" onClick={() => openImageModal(resultUrl)}>
                      <img
                        src={resultUrl}
                        alt="处理结果"
                        className="max-w-full max-h-full object-contain"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                        <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" size={24} />
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-400 text-center">
                      <ImageIcon size={48} className="mx-auto mb-2" />
                      <p>处理后的图片将显示在这里</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 图片放大弹窗 */}
      {showImageModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          style={{ touchAction: 'none' }}
        >
          <div className="relative max-w-full max-h-full overflow-hidden">
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
            >
              <X size={24} />
            </button>
            <div
              className="relative overflow-hidden cursor-grab active:cursor-grabbing"
              style={{ 
                width: '80vw', 
                height: '80vh',
                touchAction: 'none'
              }}
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img
                src={modalImageUrl}
                alt="放大预览"
                className="absolute top-1/2 left-1/2 object-contain pointer-events-none select-none"
                style={{
                  transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  maxWidth: 'none',
                  maxHeight: 'none',
                  width: 'auto',
                  height: 'auto'
                }}
                draggable={false}
              />
            </div>
            {/* 缩放提示 */}
            <div className="absolute bottom-4 left-4 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded">
              缩放: {Math.round(scale * 100)}% | 滚轮缩放 | 拖拽移动 | 双指缩放
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WatermarkTool